import type {
  CalendarEventSummary,
  CreateCalendarEventRequest,
  MeetingConflict,
  MeetingBriefing,
  MeetingFollowUpDraft,
  MeetingResponse,
  UpdateCalendarEventRequest,
  MeetingOrgOptions,
  CalendarEventFile,
  EngagementTypeOption,
  SaveEngagementTypeRequest,
} from '@todo/shared';
import type { ApiSuccess } from '@todo/shared';
import { api, get, post, patch, put } from './client';

/** /api/calendar-events - personal meetings and reminders, not tasks. */
export const calendarApi = {
  /** Events in [from, to), mine or invited - both ISO timestamps. */
  list(from: string, to: string): Promise<CalendarEventSummary[]> {
    return get<CalendarEventSummary[]>('/calendar-events', { from, to });
  },

  /**
   * The organisations this calendar spans - the filter's options and the
   * form's dropdown. One entry for a single-org person, which is how the
   * UI knows to hide both.
   */
  myOrgs(): Promise<Array<{ orgId: string; orgName: string; isCurrent: boolean }>> {
    return get('/calendar-events/my-orgs');
  },

  /**
   * One organisation's people, stakeholders and recipient groups, for the
   * meeting form. All three change together when the org dropdown moves,
   * so they arrive together.
   */
  orgOptions(orgId: string): Promise<MeetingOrgOptions> {
    return get<MeetingOrgOptions>('/calendar-events/options', { orgId });
  },

  byId(id: string): Promise<CalendarEventSummary> {
    return get<CalendarEventSummary>(`/calendar-events/${id}`);
  },

  /**
   * Who is already booked in this window - the organiser plus whoever is
   * being invited. Advisory: an overlap never stops a meeting being saved.
   */
  conflicts(params: {
    startsAt: string;
    endsAt: string;
    attendeeIds?: string[];
    excludeEventId?: string;
  }): Promise<MeetingConflict[]> {
    return get<MeetingConflict[]>('/calendar-events/conflicts', {
      startsAt: params.startsAt,
      endsAt: params.endsAt,
      ...(params.attendeeIds?.length ? { attendeeIds: params.attendeeIds.join(',') } : {}),
      ...(params.excludeEventId ? { excludeEventId: params.excludeEventId } : {}),
    });
  },

  // ---- public engagement prep checklist (organiser only) ----
  // Each call returns the refreshed event, so the caller re-renders from
  // the response instead of re-fetching the window.

  addChecklistItem(id: string, label: string): Promise<CalendarEventSummary> {
    return post<CalendarEventSummary>(`/calendar-events/${id}/checklist`, { label });
  },

  updateChecklistItem(
    id: string,
    itemId: string,
    body: { label?: string; done?: boolean },
  ): Promise<CalendarEventSummary> {
    return patch<CalendarEventSummary>(`/calendar-events/${id}/checklist/${itemId}`, body);
  },

  async removeChecklistItem(id: string, itemId: string): Promise<CalendarEventSummary> {
    const res = await api.delete<{ data: CalendarEventSummary }>(
      `/calendar-events/${id}/checklist/${itemId}`,
    );
    return res.data.data;
  },

  // ---- the notepad and the follow-up draft ----

  /**
   * Save the notepad. Organiser only; an empty string clears it. Returns
   * the refreshed event, like the checklist calls do.
   */
  saveNotes(id: string, notes: string): Promise<CalendarEventSummary> {
    return put<CalendarEventSummary>(`/calendar-events/${id}/notes`, { notes });
  },

  /**
   * The pre-meeting briefing: who is coming, how often you have met them,
   * the related past meetings and what to raise. Computed fresh each call.
   */
  briefing(id: string): Promise<MeetingBriefing> {
    return get<MeetingBriefing>(`/calendar-events/${id}/briefing`);
  },

  /**
   * Draft a follow-up message from the notepad and the meeting's facts.
   * Returns text to edit - the API never sends anything.
   */
  followUp(id: string): Promise<MeetingFollowUpDraft> {
    return post<MeetingFollowUpDraft>(`/calendar-events/${id}/follow-up`);
  },

  /** The INVITEE answers: Accept / Decline / Not sure. */
  respond(
    id: string,
    response: Exclude<MeetingResponse, 'PENDING'>,
  ): Promise<CalendarEventSummary> {
    return post<CalendarEventSummary>(`/calendar-events/${id}/response`, { response });
  },

  create(input: CreateCalendarEventRequest): Promise<CalendarEventSummary> {
    return post<CalendarEventSummary>('/calendar-events', input);
  },

  update(id: string, input: UpdateCalendarEventRequest): Promise<CalendarEventSummary> {
    return patch<CalendarEventSummary>(`/calendar-events/${id}`, input);
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/calendar-events/${id}`);
  },

  /* ----------------------------------------------- engagement types --- */

  /** The Public Engagement picker's options, as this org defines them. */
  engagementTypes(): Promise<EngagementTypeOption[]> {
    return get<EngagementTypeOption[]>('/calendar-events/engagement-types');
  },

  createEngagementType(body: SaveEngagementTypeRequest): Promise<EngagementTypeOption> {
    return post<EngagementTypeOption>('/calendar-events/engagement-types', body);
  },

  updateEngagementType(
    typeId: string, body: SaveEngagementTypeRequest,
  ): Promise<EngagementTypeOption> {
    return patch<EngagementTypeOption>(`/calendar-events/engagement-types/${typeId}`, body);
  },

  /** Soft delete; the reply says how many engagements keep the old label. */
  async deleteEngagementType(typeId: string): Promise<{ eventsKeepingIt: number }> {
    const res = await api.delete<ApiSuccess<{ deleted: true; eventsKeepingIt: number }>>(
      `/calendar-events/engagement-types/${typeId}`,
    );
    return res.data.data;
  },

  /* ----------------------------------------------- meeting documents --- */

  /**
   * Every download URL in the result is a SHORT-LIVED SAS link, so call
   * this when the panel opens rather than holding the list in state across
   * a long session - the links expire, the rows do not.
   */
  listFiles(id: string): Promise<CalendarEventFile[]> {
    return get<CalendarEventFile[]>(`/calendar-events/${id}/files`);
  },

  /** The File goes straight into the body; the name rides the query. */
  async uploadFile(id: string, file: File): Promise<CalendarEventFile[]> {
    const res = await api.post<ApiSuccess<CalendarEventFile[]>>(
      `/calendar-events/${id}/files`,
      file,
      {
        params: { filename: file.name },
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      },
    );
    return res.data.data;
  },

  /** Returns what is LEFT, so the caller swaps its list rather than guessing. */
  async deleteFile(id: string, fileId: string): Promise<CalendarEventFile[]> {
    const res = await api.delete<ApiSuccess<CalendarEventFile[]>>(
      `/calendar-events/${id}/files/${fileId}`,
    );
    return res.data.data;
  },
};
